import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, X } from 'lucide-react';
import { onAuthLogout } from '../config/authStore';
import { useAuth } from '../context/AuthContext';

/**
 * Said once, when a session ends on its own.
 *
 * The axios layer drops the user when the refresh token is refused (it
 * expired, or a password change elsewhere revoked it). Every signed-in page
 * then turns into its signed-out version — "Log in to create and keep
 * characters" — with nothing to say why, which reads as the site losing the
 * account. This says what happened and offers the way back to the same page.
 * An explicit Log out never raises it (that path does not broadcast), and
 * signing in again puts it away.
 */
export default function SessionEndedNotice() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [ended, setEnded] = useState(false);

  useEffect(() => onAuthLogout(() => setEnded(true)), []);
  useEffect(() => { if (isAuthenticated) setEnded(false); }, [isAuthenticated]);

  if (!ended || isAuthenticated) return null;
  const onLogin = location.pathname === '/login';

  return (
    <div className="card mb-4 flex flex-wrap items-center gap-3 print:hidden" role="alert" data-testid="session-ended">
      <LogIn className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A84C' }} aria-hidden="true" />
      <p className="flex-1 min-w-[12rem] text-sm" style={{ color: 'var(--text-secondary)' }}>
        You have been signed out because your session ended. Log in again to carry on.
      </p>
      {!onLogin && (
        <Link to="/login" state={{ from: `${location.pathname}${location.search}` }} className="btn btn-primary">
          Log in
        </Link>
      )}
      <button type="button" className="p-1" onClick={() => setEnded(false)} aria-label="Dismiss" title="Dismiss">
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
